import { useEffect, useMemo, useState, useSyncExternalStore, type ReactElement } from 'react';
import { creerClient } from '../../api/client.js';
import type { Ecran } from '../../api/contrat.js';
import { creerControleurEquipe } from './controleur.js';
import { FacturationEquipe } from './FacturationEquipe.js';
import { ListeFacturationReelle } from './ListeFacturation.js';
import { creerSource } from './source.js';
import { CarteSupplements } from './supplements/CarteSupplements.js';
import { CarteCompensations } from './compensations/CarteCompensations.js';
import { creerSourceCompensations, PERMISSION_LIRE_COMPENSATIONS } from './compensations/source.js';
import { creerSourceSupplements, PERMISSION_ECRIRE_SUPPLEMENTS } from './supplements/source.js';
import { CarteDevis } from './devis/CarteDevis.js';
import { creerSourceDevis, PERMISSION_ECRIRE_DEVIS, type FactureDetaillee } from './devis/source.js';

/** La facturation s'ouvre depuis le dossier avec `?dossier=<id>`. Sans identifiant, l'écran invite à choisir un dossier. */
export function dossierDepuisAdresse(recherche: string): string | null {
  return new URLSearchParams(recherche).get('dossier') || null;
}

const adresseCourante = (): string => (typeof window === 'undefined' ? '' : window.location.search);

/**
 * D08 · Facturation : sans dossier ouvert, la liste de tous les dossiers (comme la démo) ; avec `?dossier=<id>`, la facturation de ce
 * dossier. L'écran branché dans le registre de la coquille.
 */
export function EcranFacturation({ ecran }: { ecran: Ecran }): ReactElement {
  if (dossierDepuisAdresse(adresseCourante()) === null) {
    return (
      <div data-ecran={ecran.id}>
        <ListeFacturationReelle />
      </div>
    );
  }
  return <FacturationDuDossier ecran={ecran} />;
}

function FacturationDuDossier({ ecran }: { ecran: Ecran }): ReactElement {
  const controleur = useMemo(
    () =>
      creerControleurEquipe(
        creerSource(creerClient({ fetch: (...args) => globalThis.fetch(...args) })),
        dossierDepuisAdresse(adresseCourante()),
      ),
    [],
  );
  useEffect(() => {
    void controleur.charger();
  }, [controleur]);
  const etat = useSyncExternalStore(controleur.abonner, controleur.etat, controleur.etat);
  return (
    <div data-ecran={ecran.id} style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
      <FacturationEquipe etat={etat} actions={controleur.actions} />
      {/* ARB-FACT-07 : les cartes du dossier ne s'affichent qu'une fois la facturation lue ; sur un dossier inexistant ou une lecture
          refusée, l'écran ne porte qu'une alerte. */}
      {etat.statut === 'pret' ? (
        <SupplementsDuDossier
          dossierId={dossierDepuisAdresse(adresseCourante()) as string}
          factures={etat.donnees?.facturation?.invoices ?? []}
        />
      ) : null}
    </div>
  );
}

/**
 * PARITE-5 : « Suppléments en un clic » (RG-25), sous la facturation du dossier, branchés sur l'API réelle. Le droit d'écrire vient
 * des permissions de la session (le serveur refuse de toute façon) ; sa panne n'empêche pas la lecture.
 */
function SupplementsDuDossier({ dossierId, factures }: { dossierId: string; factures: readonly FactureDetaillee[] }): ReactElement {
  const client = useMemo(() => creerClient({ fetch: (...args) => globalThis.fetch(...args) }), []);
  const source = useMemo(() => creerSourceSupplements(client, dossierId), [client, dossierId]);
  const sourceCompensations = useMemo(() => creerSourceCompensations(client, dossierId), [client, dossierId]);
  const sourceDevis = useMemo(() => creerSourceDevis(client, dossierId), [client, dossierId]);
  const [permissions, setPermissions] = useState<readonly string[] | null>(null);
  useEffect(() => {
    let actif = true;
    client
      .appeler<{ permissions?: string[] }>('auth.moi')
      .then((s) => actif && setPermissions(Array.isArray(s?.permissions) ? s.permissions : []))
      .catch(() => actif && setPermissions([]));
    return () => {
      actif = false;
    };
  }, [client]);
  return (
    <>
      {/* Lignes détaillées (30/10/2026) : désignation complète sous chaque ligne de devis et de facture, PDF, nouveau devis. */}
      <CarteDevis source={sourceDevis} peutEcrire={permissions?.includes(PERMISSION_ECRIRE_DEVIS) ?? false} factures={factures} />
      <CarteSupplements source={source} peutEcrire={permissions?.includes(PERMISSION_ECRIRE_SUPPLEMENTS) ?? false} />
      {/* PARITE-5 : compensations entre héritiers (RG-28), en lecture, pour qui a le droit de les lire. */}
      {permissions?.includes(PERMISSION_LIRE_COMPENSATIONS) ? <CarteCompensations source={sourceCompensations} /> : null}
    </>
  );
}
